// 模糊档位文案（EP-004-S5 / GDD 02 §②-2.5）。
// 阈值严格 0.15 / 0.40 / 0.70 / 0.90（与 GDD §②-2.5 一致）。
// 文案不含任何阿拉伯数字（B1 硬约束）；progress 仅作 JS 索引，绝不进 DOM。
// t >= 1.0 返回空串（不渲染任何文案，进入揭晓流）。

export const FUZZY_TEXT: readonly string[] = [
  '刚出门', // 档 1 [0, 0.15)
  '它应该走了一会儿了', // 档 2 [0.15, 0.40)
  '大约在山那边玩', // 档 3 [0.40, 0.70)
  '天色好像暗了一点', // 档 4 [0.70, 0.90)
  '快到家了吧', // 档 5 [0.90, 1.0)
];

export function fuzzyStatusText(progress: number): string {
  if (progress >= 1) return '';
  if (progress < 0.15) return FUZZY_TEXT[0] as string;
  if (progress < 0.4) return FUZZY_TEXT[1] as string;
  if (progress < 0.7) return FUZZY_TEXT[2] as string;
  if (progress < 0.9) return FUZZY_TEXT[3] as string;
  return FUZZY_TEXT[4] as string;
}

// 粗粒度回家倒计时（老板 2026-09-15 拍板：中文粗粒度档，保 B1 无阿拉伯数字）。
//
// ⚠️🆕 P6-POLISH-ENG-002（07 §A.8）—— 这个函数**按 progress 比例**分档，
//   注释原本就写着"以 60 分钟标准行程估算"。旅行时长上限改成 6 小时后它就**开始说谎**：
//     6h 旅行、progress=0.1（刚出门 36min）→ 真实剩余 5h24min，却显示「回来大概还要一个多钟头」。
//   ⇒ 新代码一律改用下方的 `remainingCountdownText(remainingMs)`（按**绝对剩余毫秒**分档）。
//   `coarseCountdownText` 仅为兼容保留导出，UI 已不再调用（见 MainView）。
//   保留理由：可能有测试 / 外部脚本引用；删除需要一并评估引用面，本轮不做。
export const COUNTDOWN_TEXT: readonly string[] = [
  '回来大概还要一个多钟头', // 档 1 [0, 0.25)   剩余 > 45 分钟（旧：按 60min 估算）
  '约莫还有大半个钟头', // 档 2 [0.25, 0.5)  剩余约 30–45 分钟
  '差不多还有半个钟头', // 档 3 [0.5, 0.67)  剩余约 20–30 分钟
  '还有一两刻钟', // 档 4 [0.67, 0.83) 剩余约 10–20 分钟
  '约莫一刻钟就到家了', // 档 5 [0.83, 0.92) 剩余约 5–10 分钟
  '说话间就到家了', // 档 6 [0.92, 1.0)  剩余 < 5 分钟
];

export function coarseCountdownText(progress: number): string {
  if (progress >= 1) return '';
  if (progress < 0.25) return COUNTDOWN_TEXT[0] as string;
  if (progress < 0.5) return COUNTDOWN_TEXT[1] as string;
  if (progress < 0.67) return COUNTDOWN_TEXT[2] as string;
  if (progress < 0.83) return COUNTDOWN_TEXT[3] as string;
  if (progress < 0.92) return COUNTDOWN_TEXT[4] as string;
  return COUNTDOWN_TEXT[5] as string;
}

// 🆕 P6-POLISH-ENG-002（07 §A.8）：按**绝对剩余毫秒**分档的倒计时文案（旅行时长 30min–6h 通用）。
//   progress（相对进度）只适合描述"走到哪儿了"，不能拿来估算还要多久 —— 时长一变，比例就失去锚点。
//   全部无阿拉伯数字（B1 保持）。新增 2 条、沿用 5 条。
const MINUTE = 60_000;
const REMAINING_TEXT = {
  veryLong: '还要很久才回来', // > 3h       🆕
  aboutTwoHours: '大概还要两个多钟头', // 1.5–3h    🆕
  aboutOneHour: '回来大概还要一个多钟头', // 45min–1.5h 沿用旧档 1
  mostOfHalfHour: '约莫还有大半个钟头', // 30–45min  沿用旧档 2
  aboutHalfHour: '差不多还有半个钟头', // 20–30min  沿用旧档 3
  quarterOrTwo: '还有一两刻钟', // 10–20min  沿用旧档 4
  aboutQuarter: '约莫一刻钟就到家了', // 5–10min   沿用旧档 5
  almostHome: '说话间就到家了', // < 5min    沿用旧档 6
} as const;

/** remainingMs = 绝对剩余毫秒。<= 0（已到点/脏值）返回空串，与 progress>=1 的旧行为对齐。 */
export function remainingCountdownText(remainingMs: number): string {
  if (!Number.isFinite(remainingMs) || remainingMs <= 0) return '';
  if (remainingMs > 3 * 60 * MINUTE) return REMAINING_TEXT.veryLong;
  if (remainingMs > 90 * MINUTE) return REMAINING_TEXT.aboutTwoHours;
  if (remainingMs > 45 * MINUTE) return REMAINING_TEXT.aboutOneHour;
  if (remainingMs > 30 * MINUTE) return REMAINING_TEXT.mostOfHalfHour;
  if (remainingMs > 20 * MINUTE) return REMAINING_TEXT.aboutHalfHour;
  if (remainingMs > 10 * MINUTE) return REMAINING_TEXT.quarterOrTwo;
  if (remainingMs > 5 * MINUTE) return REMAINING_TEXT.aboutQuarter;
  return REMAINING_TEXT.almostHome;
}
